import * as Sentry from '@sentry/react';

const FAILURE_WINDOW_MS = 60_000; // 60 seconds
const FAILURE_THRESHOLD = 3;

let consecutiveFailures = 0;
let firstFailureAt = 0;

/**
 * Record a network failure (TypeError = likely CORS block).
 * Fires a fatal Sentry alert after 3 consecutive failures within 60s.
 * A successful request resets the counter (see recordNetworkSuccess).
 */
export function recordNetworkFailure(): void {
  const now = Date.now();

  if (consecutiveFailures === 0) {
    firstFailureAt = now;
  }

  // If first failure is outside the window, restart the count
  if (now - firstFailureAt >= FAILURE_WINDOW_MS) {
    consecutiveFailures = 0;
    firstFailureAt = now;
  }

  consecutiveFailures++;

  if (consecutiveFailures >= FAILURE_THRESHOLD) {
    Sentry.captureException(
      new Error(
        `Mass API network failure detected: ${consecutiveFailures} consecutive failures in ${FAILURE_WINDOW_MS / 1000}s — possible CORS misconfiguration`,
      ),
      {
        tags: { component: 'network_detector', cors_suspected: 'true' },
        level: 'fatal',
      },
    );
    // Reset to avoid alert spam
    consecutiveFailures = 0;
    firstFailureAt = 0;
  }
}

/**
 * Record a successful API call. Resets the consecutive failure counter
 * so intermittent blips don't trigger the CORS alert.
 */
export function recordNetworkSuccess(): void {
  consecutiveFailures = 0;
  firstFailureAt = 0;
}

// Exported for tests only
export function resetFailures(): void {
  consecutiveFailures = 0;
  firstFailureAt = 0;
}
